import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import { Tooltip, Zoom } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useEffect, useRef, useState } from "react";

const RESULT_TOOLTIP_MS = 1500;

type CopyState = "idle" | "copying" | "copied" | "failed";

export function CopyButton({
  getText,
}: {
  getText: () => string | null | Promise<string | null>;
}) {
  const [state, setState] = useState<CopyState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const finish = (result: CopyState) => {
    setState(result);
    if (result === "idle") return;
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setState("idle"), RESULT_TOOLTIP_MS);
  };

  const copy = async () => {
    if (state === "copying") return;
    setState("copying");
    let empty = false;
    const textPromise = Promise.resolve(getText()).then((text) => {
      if (!text) {
        empty = true;
        throw new Error("nothing to copy");
      }
      return text;
    });
    try {
      if (typeof ClipboardItem !== "undefined") {
        await window.navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": textPromise.then(
              (text) => new Blob([text], { type: "text/plain" }),
            ),
          }),
        ]);
      } else {
        await window.navigator.clipboard.writeText(await textPromise);
      }
      finish("copied");
    } catch {
      finish(empty ? "idle" : "failed");
    }
  };

  return (
    <Tooltip
      title={state === "failed" ? "Copy failed" : "Copied"}
      open={state === "copied" || state === "failed"}
      TransitionComponent={Zoom}
      placement="top"
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
    >
      <IconButton
        aria-label="copy value"
        sx={{ color: "#475467", padding: "5px" }}
        onClick={copy}
      >
        {state === "copying" ? (
          <CircularProgress size={20} thickness={5} sx={{ color: "#475467" }} />
        ) : (
          <CopyAllOutlinedIcon sx={{ width: "20px", height: "20px" }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
